import React from "react";
import { useTranslation } from "react-i18next";
import GenericTable from "@surgia/ui/containers/table/generic-table";
import Badge from "@surgia/ui/components/badge";
import productIcon from "@surgia/ui/icons/catalog/product.svg";
import copyIcon from "@surgia/ui/icons/actions/copy.svg";
import brandIcon from "@surgia/ui/icons/catalog/brand.svg";
import {
  TableColumn,
  TenantsTableProps,
} from "@surgia/ui/containers/table/constants";
import { useNavigate } from "react-router-dom";
import { ADMIN_PROTECTED_ROUTES } from "../../routes";

const getStaffUrl = () => {
  const keycloakIssuer = import.meta.env.VITE_KEYCLOAK_ISSUER;

  if (keycloakIssuer?.includes("-t")) {
    return "https://staff-test.dentacartscloud.net";
  } else if (keycloakIssuer?.includes("-s")) {
    return "https://staff-staging.dentacartscloud.net";
  } else if (keycloakIssuer?.includes("-d")) {
    return "https://staff-develop.dentacartscloud.net";
  } else {
    return "https://staff.dentacarts.com";
  }
};

const TenantsTable: React.FC<TenantsTableProps> = ({
  data,
  loading,
  currentPage,
  totalPages,
  onPageChange,
  sortState,
  onSort,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Business-specific column configuration
  const columns: TableColumn[] = [
    {
      key: "sku",
      translationKey: "shared.table.sku",
      sortable: true,
      sortKey: "id",
      render: (
        value: {
          surgiaProductSKU: string;
          tenantProductSKU: string;
        },
        row: any
      ) => (
        <div className="relative group">
          <div
            className="cursor-pointer text-purple-700 hover:text-purple-700/80 transition-colors"
            onClick={() =>
              navigate(
                ADMIN_PROTECTED_ROUTES.PRODUCT_DETAILS?.replace(
                  ":id",
                  row?.tenant_id
                )
                  .replace(":tenantName", row?.tenant_name)
                  .replace(":productId", row?.id)
                  .replace(":surgiaProductName", row?.name?.surgiaProductName)
              )
            }
          >
            <p className="text-[14px] truncate max-w-[150px]">
              {value.surgiaProductSKU}
            </p>
            <p className="table-row-sub truncate max-w-[150px]">
              {value.tenantProductSKU}
            </p>
          </div>
          <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 bg-gray-900 text-white text-sm rounded opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50 min-w-max">
            {`Edit ${value.surgiaProductSKU}`}
          </div>
        </div>
      ),
    },
    {
      key: "name",
      translationKey: "shared.table.product",
      sortable: false,
      render: (value: {
        surgiaProductName: string;
        tenantProductName: string;
        surgiaProductImage: string;
      }) => (
        <div className="flex items-center gap-2 table-row-main">
          <img
            src={value.surgiaProductImage ?? productIcon}
            alt="surgiaProductImage"
            className="rounded-full w-[40px] h-[40px] shadow-[0_0_0_0.5px_rgba(0,0,0,0.1)]"
          />
          <div>
            <p className="table-row-main max-w-[150px]">
              {value.surgiaProductName}
            </p>
            <p className="table-row-main max-w-[150px]">
              {value.tenantProductName}
            </p>
          </div>
        </div>
      ),
    },
    {
      key: "variation",
      translationKey: "shared.table.variationGroup",
      sortable: false,
      render: (value: string) => (
        <div className="flex items-center justify-center">
          <Badge variant="VARIATION" size="sm">
            {value}
          </Badge>
        </div>
      ),
    },
    {
      key: "category",
      translationKey: "shared.table.category",
      sortable: false,
      align: "left",
      render: (value: {
        surgiaFirstCategory: string;
        surgiaSecondCategory: string;
        surgiaThirdCategory: string;
      }) => (
        <div className="flex items-center gap-2">
          <Badge variant="firstCategory" size="sm">
            {value.surgiaFirstCategory}
          </Badge>
          {value.surgiaSecondCategory && (
            <Badge variant="secondCategory" size="sm">
              {value.surgiaSecondCategory}
            </Badge>
          )}
          {value.surgiaThirdCategory && (
            <Badge variant="thirdCategory" size="sm">
              {value.surgiaThirdCategory}
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: "brand",
      translationKey: "shared.table.brand",
      sortable: true,
      sortKey: "brand",
      render: (value: {
        surgiaBrandName: string;
        surgiaBrandImage: string;
      }) => (
        <div className="flex items-center gap-[12px] w-[200px]">
          <img src={brandIcon} alt={`${value.surgiaBrandName}-image`} />
          <p className="table-row-main max-w-[100px]">
            {value.surgiaBrandName}
          </p>
        </div>
      ),
    },
    {
      key: "price",
      translationKey: "shared.table.price",
      sortable: true,
      sortType: "string",
      align: "left",
      sortKey: "tenantProductPrice",
      render: (value: {
        price: number;
        currency: { id: number; code: string };
      }) => (
        <p>
          {value.price} {value.currency?.code || ""}
        </p>
      ),
    },
    {
      key: "status",
      translationKey: "shared.table.status",
      sortable: true,
      align: "left",
      sortKey: "status",
      render: (value: boolean) => (
        <Badge variant={value ? "PUBLISHED" : "default"} rounded="full">
          {value ? t("shared.table.published") : t("shared.table.draft")}
        </Badge>
      ),
    },
    {
      key: "actions",
      translationKey: "shared.table.actions",
      sortable: false,
      render: (_: any, tenant: any) => {
        return (
          <div className="flex items-center gap-1 w-[50px]">
            <img
              src={copyIcon}
              alt={t("shared.table.copy")}
              className="cursor-pointer"
              onClick={() => {
                window.open(
                  `${getStaffUrl()}/dashboard/update-product/${tenant?.id}?tab=basic-data`,
                  "_blank"
                );
              }}
            />
          </div>
        );
      },
    },
  ];

  return (
    <GenericTable
      columns={columns}
      data={data}
      loading={loading}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={onPageChange}
      sortState={sortState}
      onSort={onSort}
    />
  );
};

export default TenantsTable;
